import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import Calendar from 'react-calendar';
import dayjs from 'dayjs';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import KpiCard from './components/KpiCard';
import { KpiCardSkeleton } from './components/CardSkeleton';
import EventosTab from './tabs/EventosTab';
import VendasTab from './tabs/VendasTab';
import IngressosTab from './tabs/IngressosTab';
import ProdutosTab from './tabs/ProdutosTab';
import CuponsTab from './tabs/CuponsTab';
import dashboardStatistics from 'api/requests/dashboardStatistics';
import { Calendar as CalendarIcon } from 'lucide-react';
import { panelAccent } from 'theme/panelAccent';

const tabKeys = {
    eventos: 'eventos',
    vendas: 'vendas',
    ingressos: 'ingressos',
    produtos: 'produtos',
    cupons: 'cupons',
};

const tabs = [
    { name: tabKeys.eventos, label: 'Eventos' },
    { name: tabKeys.vendas, label: 'Vendas' },
    { name: tabKeys.ingressos, label: 'Ingressos' },
    { name: tabKeys.produtos, label: 'Produtos' },
    { name: tabKeys.cupons, label: 'Cupons' },
];

const periodOptions = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: '7d', label: '7D' },
    { value: '14d', label: '14D' },
    { value: '30d', label: '30D' },
    { value: 'all', label: 'Todos' },
];

const kpiGradients = [
    "linear-gradient(135deg, #690ce4 0%, #a855f7 100%)",
    "linear-gradient(135deg, #3F6BC6 0%, #4F86F7 100%)",
    "linear-gradient(135deg, #2BC866 0%, #41D679 100%)",
    "linear-gradient(135deg, #E7505A 0%, #ff6b6b 100%)",
];

export default function Statistics() {
    const [selectedTab, setSelectedTab] = useState(tabKeys.eventos);
    const [selectedPeriod, setSelectedPeriod] = useState('all');
    const [showCalendar, setShowCalendar] = useState(false);
    const [customRange, setCustomRange] = useState(null);
    const [kpis, setKpis] = useState(null);
    const [kpisLoading, setKpisLoading] = useState(true);

    const calendarRef = useRef(null);

    const periodParams = useMemo(() => {
        if (selectedPeriod === 'custom' && customRange) {
            return {
                start_date: dayjs(customRange[0]).format('YYYY-MM-DD'),
                end_date: dayjs(customRange[1]).format('YYYY-MM-DD'),
            };
        }
        if (selectedPeriod !== 'custom') {
            return { period: selectedPeriod };
        }
        return null;
    }, [selectedPeriod, customRange]);

    useEffect(() => {
        if (!periodParams) return;

        setKpisLoading(true);
        dashboardStatistics.getKpis(periodParams)
            .then(response => setKpis(response.data))
            .catch(error => console.error('Erro ao carregar KPIs:', error))
            .finally(() => setKpisLoading(false));
    }, [periodParams]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function handleCalendarChange(range) {
        if (!Array.isArray(range) || range.length < 2) return;

        const diffDays = dayjs(range[1]).diff(dayjs(range[0]), 'day');
        if (diffDays > 30) {
            alert('O intervalo maximo e de 30 dias.');
            return;
        }

        setCustomRange(range);
        setSelectedPeriod('custom');
        setShowCalendar(false);
    }

    return (
        <>
            <Header>
                <HeaderRight>
                    <PeriodSelector>
                        <CalendarButton
                            active={selectedPeriod === 'custom'}
                            onClick={() => {
                                setSelectedPeriod('custom');
                                setShowCalendar(!showCalendar);
                            }}
                        >
                            <CalendarIcon size={16} />
                        </CalendarButton>

                        <PeriodDivider />

                        {periodOptions.map(option => (
                            <PeriodPill
                                key={option.value}
                                active={option.value === selectedPeriod}
                                onClick={() => {
                                    setSelectedPeriod(option.value);
                                    setCustomRange(null);
                                    setShowCalendar(false);
                                }}
                            >
                                {option.label}
                            </PeriodPill>
                        ))}
                    </PeriodSelector>

                    {showCalendar && (
                        <CalendarPopover ref={calendarRef}>
                            <Calendar
                                selectRange={true}
                                value={customRange}
                                onChange={handleCalendarChange}
                                maxDate={new Date()}
                            />
                        </CalendarPopover>
                    )}
                </HeaderRight>
            </Header>

            <KpiGrid>
                {kpisLoading ? (
                    kpiGradients.map((gradient, i) => (
                        <KpiCardSkeleton key={i} gradient={gradient} />
                    ))
                ) : kpis ? (
                    <>
                        <KpiCard
                            label="Total de eventos"
                            value={kpis.eventosAtivos}
                            trend={kpis.eventosAtivosTrend}
                            gradient={kpiGradients[0]}
                        />
                        <KpiCard
                            label="Total de Cidades"
                            value={kpis.totalCidades}
                            trend={kpis.totalCidadesTrend}
                            gradient={kpiGradients[1]}
                        />
                        <KpiCard
                            label="Receita Total"
                            value={currency.format(kpis.receitaTotal)}
                            trend={kpis.receitaTotalTrend}
                            gradient={kpiGradients[2]}
                        />
                        <KpiCard
                            label="Participantes"
                            value={kpis.participantes >= 1000 ? `${(kpis.participantes / 1000).toFixed(1)}k` : kpis.participantes}
                            trend={kpis.participantesTrend}
                            gradient={kpiGradients[3]}
                        />
                    </>
                ) : null}
            </KpiGrid>

            <Container>
                <TabsContainer>
                    {tabs.map(tab => (
                        <TabItem
                            key={tab.name}
                            active={selectedTab === tab.name}
                            onClick={() => setSelectedTab(tab.name)}
                        >
                            {tab.label}
                        </TabItem>
                    ))}
                </TabsContainer>

                <TabContent>
                    {selectedTab === tabKeys.eventos && <EventosTab periodParams={periodParams} />}
                    {selectedTab === tabKeys.vendas && <VendasTab periodParams={periodParams} />}
                    {selectedTab === tabKeys.ingressos && <IngressosTab periodParams={periodParams} />}
                    {selectedTab === tabKeys.produtos && <ProdutosTab periodParams={periodParams} />}
                    {selectedTab === tabKeys.cupons && <CuponsTab periodParams={periodParams} />}
                </TabContent>
            </Container>
        </>
    );
}

const Container = styled(Column)`
    width: 100%;
    padding: 0;
    background: #FAFBFD;
    min-height: 100vh;
`;

const Header = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 0;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 12px;
`;

const HeaderRight = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;

    @media screen and (min-width: ${breakpoints.lg}) {
        position: absolute;
        margin-top: -90px;
    }
`;

const PeriodSelector = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border: 1px solid #e8e8ef;
    border-radius: 12px;
    background: ${colors.neutralLight};
    user-select: none;
`;

const CalendarButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background: ${({ active }) => active ? '#F0E6FA' : 'transparent'};
    color: ${({ active }) => active ? panelAccent(colors.secondaryPurple) : '#9896A3'};
    transition: all 0.2s ease;

    &:hover {
        background: #F0E6FA;
        color: ${panelAccent(colors.secondaryPurple)};
    }
`;

const PeriodDivider = styled.div`
    width: 1px;
    height: 20px;
    background: #e8e8ef;
    margin: 0 2px;
`;

const PeriodPill = styled.button`
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    transition: all 0.2s ease;
    background: ${({ active }) => active ? panelAccent(colors.secondaryPurple) : 'transparent'};
    color: ${({ active }) => active ? '#fff' : colors.nightDark};

    &:hover {
        background: ${({ active }) => active ? panelAccent(colors.secondaryPurple) : '#f5f4f8'};
    }
`;

const CalendarPopover = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: ${colors.neutralLight};
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 20;
    padding: 8px;

    .react-calendar {
        border: none;
        font-family: inherit;
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        right: auto;
        left: 50%;
        transform: translateX(-50%);
    }
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 20px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const TabsContainer = styled(Row)`
    background: ${colors.neutralLight};
    border-radius: 8px;
    padding: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
    margin-bottom: 20px;
    overflow-x: auto;
`;

const TabItem = styled.div`
    padding: 10px 20px;
    cursor: pointer;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: ${({ active }) => active ? panelAccent(colors.secondaryPurple) : colors.night};
    background: ${({ active }) => active ? '#F3E8FF' : 'transparent'};
    border-radius: 6px;
    transition: all 0.2s ease;
    white-space: nowrap;
    flex: 1;

    &:hover {
        color: ${panelAccent(colors.secondaryPurple)};
    }
`;

const TabContent = styled.div`
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;
